import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import { ServiceListSchema, BreadcrumbSchema } from "../components/SchemaMarkup";
import {
  ArrowRight,
  Target,
  Cog,
  LineChart,
  Users,
  Globe,
  Megaphone,
  Workflow,
  Bot,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const ServicesPage = () => {
  const services = [
    {
      icon: Target,
      title: "Business Growth Strategy",
      description: "Strategic planning and implementation to accelerate your business growth with clear roadmaps and measurable milestones.",
      slug: "business-growth-strategy",
      features: ["Growth roadmap", "Market analysis", "KPI dashboards", "Implementation support"],
    },
    {
      icon: Cog,
      title: "Marketing Automation",
      description: "Build intelligent systems that nurture leads, convert prospects, and scale your marketing without scaling your team.",
      slug: "marketing-automation",
      features: ["CRM setup", "Email sequences", "Multi-channel automation", "Lead nurturing"],
    },
    {
      icon: LineChart,
      title: "Sales Funnels & CRM",
      description: "Convert more leads with optimized funnels and intelligent CRM systems that guide prospects to purchase.",
      slug: "sales-funnels-crm",
      features: ["Funnel design", "CRM implementation", "Pipeline automation", "Lead scoring"],
    },
    {
      icon: Users,
      title: "Lead Generation",
      description: "Systematic lead generation that consistently fills your pipeline with qualified prospects ready to buy.",
      slug: "lead-generation",
      features: ["Lead magnets", "Landing pages", "Outreach sequences", "Lead qualification"],
    },
    {
      icon: Globe,
      title: "SEO & Content",
      description: "Dominate search rankings with strategic SEO and content systems that establish your authority.",
      slug: "seo-content",
      features: ["Technical SEO", "Content strategy", "Link building", "Local SEO"],
    },
    {
      icon: Megaphone,
      title: "Paid Media",
      description: "High-ROI advertising campaigns on Meta and Google that deliver consistent results and predictable growth.",
      slug: "paid-media",
      features: ["Meta Ads", "Google Ads", "Retargeting", "Conversion tracking"],
    },
    {
      icon: Workflow,
      title: "Workflow Automation",
      description: "Automate repetitive internal processes to increase efficiency and free your team for strategic work.",
      slug: "workflow-automation",
      features: ["Process automation", "Tool integration", "Data workflows", "Team efficiency"],
    },
    {
      icon: Bot,
      title: "AI-Assisted Operations",
      description: "Leverage AI strategically to enhance operations and scale capabilities without scaling headcount.",
      slug: "ai-operations",
      features: ["AI strategy", "Tool selection", "Implementation", "Team training"],
    },
    {
      icon: Globe,
      title: "Web & App Development",
      description: "Custom-coded websites and web applications built for performance, SEO, and conversions. React, Next.js, and modern frameworks.",
      slug: "web-app-development",
      features: ["Custom websites", "Web applications", "Mobile-first design", "SEO architecture"],
    },
    {
      icon: Sparkles,
      title: "ChatGPT Ads & GEO",
      description: "Be found everywhere your customers search — ChatGPT Ads, Generative Engine Optimization, SEO, and Google Ads managed by one agency.",
      slug: "chatgpt-ads",
      features: ["ChatGPT Ads", "GEO / AEO", "AI visibility strategy", "Multi-channel search"],
      highlight: true,
    },
  ];

  return (
    <div className="pt-32" data-testid="services-page">
      <SEO 
        title="Our Services | SEO, Automation & Growth"
        description="Comprehensive SEO, marketing automation, lead generation, CRM setup, sales funnels, paid media, workflow automation, AI operations, and web development services."
        keywords="SEO services, marketing automation services, lead generation agency, CRM setup, sales funnels, paid media management, workflow automation, AI marketing services"
      />
      <ServiceListSchema services={services} />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Services" }]} />
      <Breadcrumb items={[{ name: "Services" }]} />
      {/* Hero Section */}
      <section className="section pb-0" data-testid="services-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-blue mb-4">Our Services</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                SEO, Marketing Automation & Lead Generation Services
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Comprehensive solutions designed to scale your business efficiently. From strategy to implementation, we build the systems that power sustainable growth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section" data-testid="services-grid">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className={`card service-card block p-8 h-full ${service.highlight ? 'ring-2 ring-optima-cyan/50 relative overflow-hidden' : ''}`}
                  data-testid={`service-${service.slug}`}
                >
                  {service.highlight && (
                    <span className="absolute top-4 right-4 px-3 py-1 bg-optima-cyan text-white text-xs font-bold rounded-full uppercase tracking-wide">New</span>
                  )}
                  <div className="flex items-start gap-6">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${service.highlight ? 'bg-gradient-to-br from-optima-cyan to-optima-blue' : 'bg-gradient-to-br from-optima-blue to-optima-cyan'}`}>
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-semibold text-optima-dark mb-2">
                        {service.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{service.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {service.features.map((feature, i) => (
                          <span
                            key={feature}
                            className="px-3 py-1 bg-slate-50 rounded-full text-xs text-slate-600"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                      <span className="inline-flex items-center gap-2 text-optima-blue font-medium">
                        Learn more <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="services-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              Not Sure Where to Start?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Book a free growth audit and we'll help you identify the highest-impact opportunities for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
                data-testid="services-book-call"
              >
                Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors"
                data-testid="services-view-pricing"
              >
                View Pricing
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/case-studies" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Case Studies</Link>
              <Link to="/software" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Our Software</Link>
              <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Our Blog</Link>
              <Link to="/client-onboarding" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Client Onboarding</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
